import React from 'react';
import * as THREE from 'three';
import {
  Canvas, ThreeEvent, invalidate, useFrame, useThree
} from '@react-three/fiber';
import { fromPairs, map } from 'ramda';
import { i18n } from 'i18n';

import viewCubeLabel from './viewCubeLabels';
import {
  COMPASS_POINTS, CompassPoint, CubePiece, CubePieceKind, LETTER_SIZE, RING_MID, buildCubePieces, buildRingGeometry, compassLetterPose
} from './viewCubeGeometry';
import {
  CameraRig, POLE_MARGIN, ViewCubeControls, animateCamera, headingQuaternion, orbitScene, ringDragAxis, screenFlip, turnScene, viewQuaternion
} from './viewCubeCamera';

export const VIEW_CUBE_SELECTOR = '[data-view-cube]';

const CANVAS_SIZE = 220;
const PIXELS_PER_UNIT = 74;
const CLICK_TOLERANCE_PX = 4;
const CUBE_DRAG_RADIANS_PER_PX = Math.PI / CANVAS_SIZE;
const FACE_TEXTURE_SIZE = 256;
const LETTER_TEXTURE_SIZE = 128;

const COLOURS = {
  outline: '#4a4a4a',
  text: '#262626',
  hover: '#7aa7e6',
  ring: '#3b3b3b',
  ringHover: '#565656',
  letter: '#cfcfcf',
};

const PIECE_COLOURS: Record<CubePieceKind, string> = {
  face: '#d6d6d6',
  edge: '#bdbdbd',
  corner: '#adadad',
};

interface ViewCubeProps {
  cameraRef: React.RefObject<THREE.Camera>;
  controlsRef: React.RefObject<ViewCubeControls>;
  invalidateRef: React.MutableRefObject<(() => void) | null>;
  zUpMode: boolean;
  centreX: number;
  bottom: number;
}

interface PointerGesture {
  onDrag: (rig: CameraRig, drag: THREE.Vector2) => void;
  onClick?: () => void;
}

type ViewCubeSceneProps = Pick<ViewCubeProps, 'cameraRef' | 'controlsRef' | 'invalidateRef' | 'zUpMode'>;

interface LabelTextureOptions {
  size: number;
  weight: number;
  fontScale: number;
  fill: string;
  background?: string;
}

const LABEL_FONT_FAMILY = '"Segoe UI", Arial, sans-serif';

const labelFont = (weight: number, pixels: number) => `${weight} ${pixels}px ${LABEL_FONT_FAMILY}`;

const createLabelTexture = (label: string, {
  size, weight, fontScale, fill, background,
}: LabelTextureOptions) => {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const context = canvas.getContext('2d');
  if (context) {
    if (background) {
      context.fillStyle = background;
      context.fillRect(0, 0, size, size);
    }
    const baseSize = size * fontScale;
    const maxWidth = size * 0.84;
    context.font = labelFont(weight, baseSize);
    const measured = context.measureText(label).width;
    context.font = labelFont(weight, measured > maxWidth ? baseSize * (maxWidth / measured) : baseSize);
    context.fillStyle = fill;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(label, size / 2, size / 2);
  }
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.anisotropy = 4;
  return texture;
};

const ViewCubeScene = ({
  cameraRef, controlsRef, invalidateRef, zUpMode,
}: ViewCubeSceneProps) => {
  const gl = useThree((state) => state.gl);
  const invalidateCube = useThree((state) => state.invalidate);
  const groupRef = React.useRef<THREE.Group>(null);
  const stopAnimationRef = React.useRef<(() => void) | null>(null);
  const endDragRef = React.useRef<(() => void) | null>(null);
  const [hovered, setHovered] = React.useState<string | null>(null);

  const pieces = React.useMemo(buildCubePieces, []);
  const ringGeometry = React.useMemo(buildRingGeometry, []);
  const letterPoses = React.useMemo(() => map((point) => compassLetterPose(point.direction), COMPASS_POINTS), []);
  const faceTextures = React.useMemo(() => fromPairs(map((piece): [string, THREE.Texture] => [
    piece.key,
    createLabelTexture(viewCubeLabel(piece.label ?? ''), {
      size: FACE_TEXTURE_SIZE, weight: 600, fontScale: 0.3, fill: COLOURS.text, background: '#ffffff',
    }),
  ], pieces.filter((piece) => piece.label))), [pieces]);
  const letterTextures = React.useMemo(() => map((point) => createLabelTexture(viewCubeLabel(point.label), {
    size: LETTER_TEXTURE_SIZE, weight: 700, fontScale: 0.8, fill: '#ffffff',
  }), COMPASS_POINTS), []);

  const getRig = (): CameraRig | null => (cameraRef.current && controlsRef.current
    ? {
      camera: cameraRef.current, controls: controlsRef.current, keepWorldUp: zUpMode, onChange: () => invalidate(),
    }
    : null);

  const animateTo = (orientationFor: (current: THREE.Quaternion) => THREE.Quaternion) => {
    const rig = getRig();
    if (!rig) return;
    stopAnimationRef.current?.();
    stopAnimationRef.current = animateCamera(rig, orientationFor(rig.camera.quaternion));
  };

  const setCursor = (cursor: string) => {
    gl.domElement.style.cursor = cursor;
  };

  const hoverHandlers = (key: string, cursor: string) => ({
    onPointerOver: (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      setHovered(key);
      if (!endDragRef.current) setCursor(cursor);
    },
    onPointerOut: (event: ThreeEvent<PointerEvent>) => {
      event.stopPropagation();
      setHovered((current) => (current === key ? null : current));
      if (!endDragRef.current) setCursor('');
    },
  });

  const trackPointer = (event: ThreeEvent<PointerEvent>, { onDrag, onClick }: PointerGesture) => {
    const start = { x: event.nativeEvent.clientX, y: event.nativeEvent.clientY };
    const gesture = { last: start, dragging: false };
    const listeners = new AbortController();

    const endDrag = () => {
      listeners.abort();
      endDragRef.current = null;
      setCursor('');
    };

    const handleMove = (moveEvent: PointerEvent) => {
      const rig = getRig();
      const position = { x: moveEvent.clientX, y: moveEvent.clientY };
      if (!rig || (!gesture.dragging && Math.hypot(position.x - start.x, position.y - start.y) <= CLICK_TOLERANCE_PX)) return;
      if (!gesture.dragging) setCursor('grabbing');
      onDrag(rig, new THREE.Vector2(position.x - gesture.last.x, position.y - gesture.last.y));
      Object.assign(gesture, { last: position, dragging: true });
    };

    const handleUp = () => {
      endDrag();
      if (!gesture.dragging) onClick?.();
    };

    stopAnimationRef.current?.();
    endDragRef.current?.();
    endDragRef.current = endDrag;
    window.addEventListener('pointermove', handleMove, { signal: listeners.signal });
    window.addEventListener('pointerup', handleUp, { signal: listeners.signal });
    window.addEventListener('pointercancel', endDrag, { signal: listeners.signal });
  };

  const handlePiecePointerDown = (piece: CubePiece) => (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    const rig = getRig();
    if (!rig || event.button !== 0) return;

    const radiansPerPx = screenFlip(rig.camera.quaternion) * CUBE_DRAG_RADIANS_PER_PX;
    trackPointer(event, {
      onDrag: (current, drag) => orbitScene(current, drag.x * radiansPerPx, -drag.y * radiansPerPx, zUpMode ? POLE_MARGIN : 0),
      onClick: () => animateTo((current) => viewQuaternion(piece.direction, current, zUpMode ? POLE_MARGIN : 0)),
    });
  };

  const handleRingPointerDown = (compassPoint: CompassPoint | null) => (event: ThreeEvent<PointerEvent>) => {
    event.stopPropagation();
    const rig = getRig();
    const group = groupRef.current;
    if (!rig || !group || event.button !== 0) return;

    const axis = ringDragAxis(group.worldToLocal(event.point.clone()), rig.camera.quaternion);
    trackPointer(event, {
      onDrag: (current, drag) => turnScene(current, axis.dot(drag) / (RING_MID * PIXELS_PER_UNIT)),
      onClick: compassPoint ? () => animateTo((current) => headingQuaternion(compassPoint.direction, current)) : undefined,
    });
  };

  useFrame(() => {
    const camera = cameraRef.current;
    const group = groupRef.current;
    if (camera && group) group.quaternion.copy(camera.quaternion).invert();
  });

  React.useEffect(() => {
    const ref = invalidateRef;
    ref.current = () => invalidateCube();
    return () => {
      ref.current = null;
    };
  }, [invalidateRef, invalidateCube]);

  React.useEffect(() => () => {
    stopAnimationRef.current?.();
    endDragRef.current?.();
    ringGeometry.dispose();
    pieces.forEach((piece) => piece.geometry.dispose());
    Object.values(faceTextures).forEach((texture) => texture.dispose());
    letterTextures.forEach((texture) => texture.dispose());
  }, [pieces, ringGeometry, faceTextures, letterTextures]);

  return (
    <group ref={groupRef}>
      {pieces.map((piece) => (
        <mesh key={piece.key} geometry={piece.geometry} onPointerDown={handlePiecePointerDown(piece)} {...hoverHandlers(piece.key, 'pointer')}>
          <meshBasicMaterial
            color={hovered === piece.key ? COLOURS.hover : PIECE_COLOURS[piece.kind]}
            map={faceTextures[piece.key] ?? null}
            polygonOffset
            polygonOffsetFactor={1}
            polygonOffsetUnits={1}
            toneMapped={false}
          />
          <lineSegments raycast={() => null}>
            <edgesGeometry args={[piece.geometry]} />
            <lineBasicMaterial color={COLOURS.outline} toneMapped={false} />
          </lineSegments>
        </mesh>
      ))}
      <mesh geometry={ringGeometry} onPointerDown={handleRingPointerDown(null)} {...hoverHandlers('ring', 'grab')}>
        <meshBasicMaterial color={hovered === 'ring' ? COLOURS.ringHover : COLOURS.ring} transparent opacity={0.9} toneMapped={false} />
      </mesh>
      {COMPASS_POINTS.map((point, index) => (
        <mesh
          key={point.label}
          position={letterPoses[index].position}
          quaternion={letterPoses[index].quaternion}
          renderOrder={1}
          onPointerDown={handleRingPointerDown(point)}
          {...hoverHandlers(point.label, 'pointer')}
        >
          <planeGeometry args={[LETTER_SIZE, LETTER_SIZE]} />
          <meshBasicMaterial
            map={letterTextures[index]}
            color={hovered === point.label ? COLOURS.hover : COLOURS.letter}
            transparent
            depthWrite={false}
            toneMapped={false}
          />
        </mesh>
      ))}
    </group>
  );
};

export const ViewCubeSync = ({ invalidateRef }: Pick<ViewCubeProps, 'invalidateRef'>) => {
  useFrame(() => invalidateRef.current?.());
  return null;
};

const ViewCube = ({
  cameraRef, controlsRef, invalidateRef, zUpMode, centreX, bottom,
}: ViewCubeProps) => (
  <div
    data-view-cube
    style={{
      position: 'absolute',
      left: centreX - CANVAS_SIZE / 2,
      bottom,
      width: CANVAS_SIZE,
      height: CANVAS_SIZE,
      clipPath: 'circle(50%)',
    }}
  >
    <Canvas
      orthographic
      frameloop="demand"
      dpr={[1, 2]}
      gl={{ alpha: true, antialias: true }}
      camera={{
        position: [0, 0, 10], zoom: PIXELS_PER_UNIT, near: 0.1, far: 100,
      }}
    >
      {/* Labels are painted into textures once, so a language change remounts the scene to repaint them. */}
      <ViewCubeScene key={i18n.locale} cameraRef={cameraRef} controlsRef={controlsRef} invalidateRef={invalidateRef} zUpMode={zUpMode} />
    </Canvas>
  </div>
);

export default ViewCube;
